/**
 * @fileoverview Centralized sync activity log store for real-time troubleshooting.
 * @module local-db/stores/syncActivityLog
 *
 * Responsibilities:
 * - Maintain an in-memory ring buffer of sync activity log entries.
 * - Provide a subscribe/emit pattern so UI components (mobile SyncMonitorPage,
 *   tenant-web SyncStatusIndicator) can display live sync diagnostics.
 * - Emit structured, human-readable messages that help end-users and developers
 *   understand what the sync engine is doing at any moment.
 *
 * Dependencies:
 * - None — this is a standalone in-memory store. Consumers import and call
 *   `addSyncLogEntry` from replicationFactory, websocketSyncClient, etc.
 *
 * Architecture:
 * - Pure in-memory ring buffer (no persistence). Entries are lost on page reload.
 * - Max 500 entries retained; oldest are evicted first.
 * - Listeners are notified synchronously on each new entry.
 *
 * Notes:
 * - This store is intentionally separate from syncStatusStore so that the log
 *   can be subscribed independently (e.g. a debug panel) without coupling to
 *   the status aggregation logic.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * @contract SyncLogLevel
 *
 * Purpose:
 * - Classify log entries by severity for color-coded display.
 *
 * Fields:
 * - 'info': Normal operational events (pull started, pull done, connected).
 * - 'warn': Degraded but recoverable (offline, reconnecting, slow sync).
 * - 'error': Failures that may cause data staleness (push failed, auth error).
 * - 'success': Positive confirmations (pushed to server, sync complete).
 *
 * Rules:
 * - Agents must use the most specific level. Do not log errors as warnings.
 */
export type SyncLogLevel = 'info' | 'warn' | 'error' | 'success';

/**
 * @contract SyncLogEntry
 *
 * Purpose:
 * - Represent a single sync activity event for display in troubleshooting UIs.
 *
 * Fields:
 * - id: Auto-incrementing unique identifier.
 * - timestamp: ISO-8601 string for when the event occurred.
 * - level: Severity classification.
 * - message: Human-readable description of the event.
 * - collection: Optional Dexie collection name related to the event.
 * - detail: Optional extra detail (error message, doc count, etc.).
 *
 * Rules:
 * - Messages must be concise and meaningful for end-users.
 *
 * Sync:
 * - In-memory only; not persisted to Dexie or server.
 */
export interface SyncLogEntry {
  id: number;
  timestamp: string;
  level: SyncLogLevel;
  message: string;
  collection?: string;
  detail?: string;
}

// ---------------------------------------------------------------------------
// Store implementation
// ---------------------------------------------------------------------------

type SyncLogListener = (entries: SyncLogEntry[]) => void;
type SingleEntryListener = (entry: SyncLogEntry) => void;

const MAX_ENTRIES = 500;
let _idCounter = 0;
const _entries: SyncLogEntry[] = [];
const _listeners = new Set<SyncLogListener>();
const _entryListeners = new Set<SingleEntryListener>();

function notify(): void {
  const snapshot = [..._entries];
  _listeners.forEach((fn) => {
    try { fn(snapshot); } catch { /* swallow listener errors */ }
  });
}

function notifyEntry(entry: SyncLogEntry): void {
  _entryListeners.forEach((fn) => {
    try { fn(entry); } catch { /* swallow listener errors */ }
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Add a log entry. Called from replicationFactory, websocketSyncClient, etc.
 */
export function addSyncLogEntry(
  level: SyncLogLevel,
  message: string,
  opts?: { collection?: string; detail?: string },
): SyncLogEntry {
  const entry: SyncLogEntry = {
    id: ++_idCounter,
    timestamp: new Date().toISOString(),
    level,
    message,
    collection: opts?.collection,
    detail: opts?.detail,
  };
  _entries.push(entry);
  if (_entries.length > MAX_ENTRIES) {
    _entries.splice(0, _entries.length - MAX_ENTRIES);
  }
  notify();
  notifyEntry(entry);
  return entry;
}

/** Convenience shortcuts */
export const syncLog = {
  info: (msg: string, opts?: { collection?: string; detail?: string }) =>
    addSyncLogEntry('info', msg, opts),
  warn: (msg: string, opts?: { collection?: string; detail?: string }) =>
    addSyncLogEntry('warn', msg, opts),
  error: (msg: string, opts?: { collection?: string; detail?: string }) =>
    addSyncLogEntry('error', msg, opts),
  success: (msg: string, opts?: { collection?: string; detail?: string }) =>
    addSyncLogEntry('success', msg, opts),
};

/**
 * Subscribe to the full log snapshot (re-emitted on every new entry).
 * Returns an unsubscribe function.
 */
export function subscribeSyncLog(fn: SyncLogListener): () => void {
  _listeners.add(fn);
  // Emit current state immediately
  fn([..._entries]);
  return () => { _listeners.delete(fn); };
}

/**
 * Subscribe to individual new entries as they arrive.
 * Useful for appending to a log view without replacing the full list.
 */
export function onSyncLogEntry(fn: SingleEntryListener): () => void {
  _entryListeners.add(fn);
  return () => { _entryListeners.delete(fn); };
}

/** Get a snapshot of all current entries. */
export function getSyncLogEntries(): SyncLogEntry[] {
  return [..._entries];
}

/** Clear all log entries. */
export function clearSyncLog(): void {
  _entries.length = 0;
  notify();
}
